import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { AlertTriangle, X } from "lucide-react";
import useAuthUser from "@/hooks/useAuthUser";
import { IS_PRODUCTION } from "@/config";
import { PATH_SETTINGS_CHANNELS } from "@/routes/paths";
import { selectBrokenConnections, useChannelsStore } from "@/store/channels/channels.store";
import { PROVIDER_META } from "@/models/channels.model";

const DISMISS_KEY = "ironpipeline.channels.alert-dismissed";

/** How often the banner re-reads the connections, so a break found mid-session shows without a reload. */
const REFRESH_MS = 5 * 60_000;

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
};

/**
 * App-wide banner when an omnichannel connection breaks (token expired,
 * access revoked) — IR-68 asks for the admin to be alerted and prompted to
 * reconnect, not only on the Channels page. Admin-only, hidden on Settings →
 * Channels where the card already shows it. It re-reads the connections every
 * few minutes and when the tab regains focus. Dismissing hides the breaks it
 * showed for the session; a new break brings it back.
 */
export default function ChannelConnectionAlert() {
  const { hasPermission } = useAuthUser();
  // Settings → Channels doesn't exist in production builds (module not ready for
  // customers), so the banner — which links there — must not show or poll either.
  const canManage = !IS_PRODUCTION && hasPermission("channels:edit");
  const { pathname } = useLocation();
  const initialized = useChannelsStore((s) => s.initialized);
  const fetchConnections = useChannelsStore((s) => s.fetchConnections);
  const broken = useChannelsStore(useShallow(selectBrokenConnections));
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    // Best effort — a failed check must never block the app.
    if (canManage && !initialized) fetchConnections().catch(() => undefined);
  }, [canManage, initialized, fetchConnections]);

  useEffect(() => {
    if (!canManage) return;
    const refresh = () => void fetchConnections(true).catch(() => undefined);
    const onFocus = () => document.visibilityState === "visible" && refresh();
    const t = setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [canManage, fetchConnections]);

  // What is broken right now; dismissing remembers exactly this, so a different break shows again.
  const signature = broken.map((c) => `${c.provider}:${c.error ?? ""}`).join("|");

  if (!canManage || dismissed === signature || broken.length === 0 || pathname.startsWith(PATH_SETTINGS_CHANNELS)) {
    return null;
  }

  const names = broken.map((c) => PROVIDER_META[c.provider].label).join(", ");
  const detail = broken.length === 1 ? broken[0].error : "Some channels stopped receiving messages.";

  const dismiss = () => {
    setDismissed(signature);
    try {
      sessionStorage.setItem(DISMISS_KEY, signature);
    } catch {
      // Storage unavailable — dismiss for this render tree only.
    }
  };

  return (
    <div className="relative mx-4 mt-3 flex items-center gap-3 rounded-xl border border-accent-red/25 bg-accent-red/10 px-3.5 py-2.5">
      <AlertTriangle className="h-4 w-4 shrink-0 text-accent-red" />
      <p className="min-w-0 flex-1 text-[12px] text-red-200">
        <span className="font-semibold text-accent-red">{names} disconnected.</span> {detail}
      </p>
      <Link
        to={PATH_SETTINGS_CHANNELS}
        className="shrink-0 rounded-lg bg-accent-red/20 px-2.5 py-1.5 text-[11.5px] font-semibold text-accent-red transition hover:bg-accent-red/30"
      >
        Reconnect
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-accent-red/70 transition hover:bg-accent-red/15 hover:text-accent-red"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
